import type { Point } from "geojson";
import z from "zod";
import type { GeoJSONPoint } from "@remi/types/geojson";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const financeErrorTypeValues = [
  "UNKNOWN",
  "STRATEGIC_PARTNER_BID_MISSING_ACTIVE_QUOTE",
  "ODDJOB_ENQUEUE_TIMEOUT",
  "DEAL_DESK_UNSUPPORTED_PARTNER_FLOW",
  "DEAL_DESK_SUNRUN_FRP_NOT_SUPPORTED",
  "DEAL_DESK_NO_ADJUSTMENT_PATH",
  "DEAL_DESK_PRICEBOOK_PRICE_MISSING",
  "DEAL_DESK_CONTRACTOR_COST_NOT_FOUND",
  "DEAL_DESK_COMP_SHINGLE_PRICE_MISSING",
  "DEAL_DESK_COST_PERCENTAGE_NOT_COMPUTED",
  "DEAL_DESK_MISSING_MARKET_GEOGRAPHIC_AREA",
  "DEAL_DESK_MISSING_MATERIALS_RATEBOOK",
  "DEAL_DESK_INVALID_WORKSITE_GEOJSON",
  "DEAL_DESK_NO_ELIGIBLE_CONTRACTORS",
  "DEAL_DESK_NO_DEFAULT_COSTS",
  "DEAL_DESK_MISSING_LINE_ITEM_ID",
  "DEAL_DESK_LINE_ITEM_COST_MISMATCH",
  "BID_NOT_FOUND",
  "HOMEOWNER_EMAIL_MISSING",
  "PROJECT_NOT_FOUND",
  "INVALID_CONTRACTOR_TYPE",
  "MARKET_NOT_FOUND_FOR_PROPERTY_ADDRESS_GEO_POINT",
  "SERVICE_AREA_NOT_FOUND_FOR_PROPERTY_ADDRESS",
  "RATEBOOK_NOT_FOUND_FOR_GEO_POINT_AND_DATE",
  "HOMEOWNER_CREDIT_NOT_FOUND",
  "HOMEOWNER_CREDIT_NOT_MUTABLE",
  "HOMEOWNER_CREDIT_NOT_APPROVABLE",
  "HOMEOWNER_CREDIT_NOT_REJECTABLE",
  "HOMEOWNER_CREDIT_NOT_MARKABLE_SENT",
  "HOMEOWNER_CREDIT_NOT_CONFIRMABLE",
  "APPEASEMENT_REQUIRES_BID_PROJECT",
  "APPEASEMENT_INVALID_REQUESTING_TEAM",
  "APPEASEMENT_INVALID_ASSOCIATED_PROJECTS",
  "APPEASEMENT_CATEGORY_REQUIRED",
  "APPEASEMENT_INVALID_GOODWILL_AMOUNT",
  "APPEASEMENT_REVIEWER_UNRESOLVED",
  "CREATE_VENDOR_CREDITS_TASK_NOT_FOUND",
  "CREATE_VENDOR_CREDITS_ATTEMPT_NOT_FOUND",
  "VENDOR_CREDIT_INVALID_ALLOCATION",
  "VENDOR_CREDIT_PROJECT_NOT_ALLOCATABLE",
  "VENDOR_CREDIT_MULTIPLE_PAYEES_FOR_PROJECT",
  "VENDOR_CREDIT_BID_NOT_FOUND_FOR_PROJECT",
  "VENDOR_CREDIT_APPEASEMENT_NOT_FOUND",
  "RATEBOOK_NOT_FOUND_FOR_MARKET_AND_DATE",
  "FINANCING_REJECTION_TICKET_MISSING",
  "SEED_FINANCING_STILL_INITIALIZING",
] as const;
const financeErrorTypeSchema = z.enum(financeErrorTypeValues);
const FinanceErrorType = financeErrorTypeSchema.enum;
type FinanceErrorType = z.infer<typeof financeErrorTypeSchema>;

export type FinanceErrors =
  | { errorType: typeof FinanceErrorType.UNKNOWN; data: undefined }
  | { errorType: typeof FinanceErrorType.ODDJOB_ENQUEUE_TIMEOUT; data: { budgetMs: number } }
  | {
      errorType: typeof FinanceErrorType.STRATEGIC_PARTNER_BID_MISSING_ACTIVE_QUOTE;
      data: { bidId: number; requestForQuoteId: number };
    }
  | {
      errorType: typeof FinanceErrorType.DEAL_DESK_UNSUPPORTED_PARTNER_FLOW;
      data: { partnerFlow: string; bidId?: number };
    }
  | { errorType: typeof FinanceErrorType.DEAL_DESK_SUNRUN_FRP_NOT_SUPPORTED; data: { bidId?: number } }
  | { errorType: typeof FinanceErrorType.DEAL_DESK_NO_ADJUSTMENT_PATH; data: { bidId?: number } }
  | {
      errorType: typeof FinanceErrorType.DEAL_DESK_PRICEBOOK_PRICE_MISSING;
      data: {
        organizationId: string;
        lineItemTypeName: string;
        marketId: number;
        pricingType?: string;
        bidId?: number;
      };
    }
  | {
      errorType: typeof FinanceErrorType.DEAL_DESK_CONTRACTOR_COST_NOT_FOUND;
      data: { lineItemTypeName: string; marketId: number; pricingType?: string; bidId?: number };
    }
  | {
      errorType: typeof FinanceErrorType.DEAL_DESK_COMP_SHINGLE_PRICE_MISSING;
      data: { organizationId: string; marketId: number; lineItemTypeName: string; bidId?: number };
    }
  | { errorType: typeof FinanceErrorType.DEAL_DESK_COST_PERCENTAGE_NOT_COMPUTED; data: { bidId?: number } }
  | { errorType: typeof FinanceErrorType.DEAL_DESK_MISSING_MARKET_GEOGRAPHIC_AREA; data: { marketId: number } }
  | {
      errorType: typeof FinanceErrorType.DEAL_DESK_MISSING_MATERIALS_RATEBOOK;
      data: { marketId: number; geographicAreaId: string };
    }
  | { errorType: typeof FinanceErrorType.DEAL_DESK_INVALID_WORKSITE_GEOJSON; data: { marketId: number } }
  | {
      errorType: typeof FinanceErrorType.DEAL_DESK_NO_ELIGIBLE_CONTRACTORS;
      data: {
        projectCategory: string;
        missingDefaultCostLineItemTypes: string[];
        claimingContractorCount: number;
        fallbackContractor: boolean;
        market?: string;
        quoteId?: string;
        projectId?: number;
      };
    }
  | {
      errorType: typeof FinanceErrorType.DEAL_DESK_NO_DEFAULT_COSTS;
      data: {
        projectCategory: string;
        missingDefaultCostLineItemTypes: string[];
        claimingContractorCount: number;
        fallbackContractor: boolean;
        market?: string;
        quoteId?: string;
        projectId?: number;
      };
    }
  | {
      errorType: typeof FinanceErrorType.DEAL_DESK_MISSING_LINE_ITEM_ID;
      data: { quoteVersionId: string; lineItemTypeName: string };
    }
  | {
      errorType: typeof FinanceErrorType.DEAL_DESK_LINE_ITEM_COST_MISMATCH;
      data: { quoteVersionId: string; quoteLevelCost: number; lineItemCostSum: number; delta: number };
    }
  | { errorType: typeof FinanceErrorType.BID_NOT_FOUND; data: { bidId: number } }
  | {
      errorType: typeof FinanceErrorType.HOMEOWNER_EMAIL_MISSING;
      data: { bidId: number; requestForQuoteId: number; financingId: string };
    }
  | { errorType: typeof FinanceErrorType.PROJECT_NOT_FOUND; data: { bidId: number; rfqId: number } }
  | { errorType: typeof FinanceErrorType.INVALID_CONTRACTOR_TYPE; data: { contractorType: unknown } }
  | {
      errorType: typeof FinanceErrorType.MARKET_NOT_FOUND_FOR_PROPERTY_ADDRESS_GEO_POINT;
      data: { propertyAddressId: string; geoPoint: Point };
    }
  | {
      errorType: typeof FinanceErrorType.SERVICE_AREA_NOT_FOUND_FOR_PROPERTY_ADDRESS;
      data: { propertyAddressId: string; geoPoint: Point };
    }
  | {
      errorType: typeof FinanceErrorType.RATEBOOK_NOT_FOUND_FOR_GEO_POINT_AND_DATE;
      data: { addressCenterPoint: GeoJSONPoint; asOfDate?: Date };
    }
  | {
      errorType: typeof FinanceErrorType.HOMEOWNER_CREDIT_NOT_FOUND;
      data: { homeownerCreditId: string };
    }
  | {
      errorType: typeof FinanceErrorType.HOMEOWNER_CREDIT_NOT_MUTABLE;
      data: { homeownerCreditId: string; status: string };
    }
  | {
      errorType: typeof FinanceErrorType.HOMEOWNER_CREDIT_NOT_APPROVABLE;
      data: { homeownerCreditId: string; status: string };
    }
  | {
      errorType: typeof FinanceErrorType.HOMEOWNER_CREDIT_NOT_REJECTABLE;
      data: { homeownerCreditId: string; status: string };
    }
  | {
      errorType: typeof FinanceErrorType.HOMEOWNER_CREDIT_NOT_MARKABLE_SENT;
      data: { homeownerCreditId: string; status: string };
    }
  | {
      errorType: typeof FinanceErrorType.HOMEOWNER_CREDIT_NOT_CONFIRMABLE;
      data: { homeownerCreditId: string; status: string };
    }
  | {
      errorType: typeof FinanceErrorType.APPEASEMENT_REQUIRES_BID_PROJECT;
      data: { bidId: number };
    }
  | {
      errorType: typeof FinanceErrorType.APPEASEMENT_INVALID_REQUESTING_TEAM;
      data: { requestingTeamId: string };
    }
  | {
      errorType: typeof FinanceErrorType.APPEASEMENT_INVALID_ASSOCIATED_PROJECTS;
      data: { bidId: number; invalidProjectIds: number[] };
    }
  | {
      errorType: typeof FinanceErrorType.APPEASEMENT_CATEGORY_REQUIRED;
      data: { bidId: number };
    }
  | {
      errorType: typeof FinanceErrorType.APPEASEMENT_INVALID_GOODWILL_AMOUNT;
      data: { bidId: number; reason: string };
    }
  | {
      errorType: typeof FinanceErrorType.APPEASEMENT_REVIEWER_UNRESOLVED;
      data: { requestingTeamId: string | null; reviewer: string; homeownerCreditId?: string; bidId?: number };
    }
  | { errorType: typeof FinanceErrorType.CREATE_VENDOR_CREDITS_TASK_NOT_FOUND; data: { taskId: string } }
  | { errorType: typeof FinanceErrorType.CREATE_VENDOR_CREDITS_ATTEMPT_NOT_FOUND; data: { taskId: string } }
  | {
      // One errorType for all allocation-validation failures; `reason` carries the
      // specific cause (negative / over-allocation / invalid-total / duplicate) and
      // `detail` the structured allocation error for diagnostics.
      errorType: typeof FinanceErrorType.VENDOR_CREDIT_INVALID_ALLOCATION;
      data: { taskId: string; reason: string; detail: unknown };
    }
  | {
      errorType: typeof FinanceErrorType.VENDOR_CREDIT_PROJECT_NOT_ALLOCATABLE;
      data: { taskId: string; projectId: number };
    }
  | {
      errorType: typeof FinanceErrorType.VENDOR_CREDIT_MULTIPLE_PAYEES_FOR_PROJECT;
      data: { taskId: string; projectId: number; payeeIds: string[] };
    }
  | {
      errorType: typeof FinanceErrorType.VENDOR_CREDIT_BID_NOT_FOUND_FOR_PROJECT;
      data: { taskId: string };
    }
  | { errorType: typeof FinanceErrorType.VENDOR_CREDIT_APPEASEMENT_NOT_FOUND; data: { appeasementId: string } }
  | {
      errorType: typeof FinanceErrorType.RATEBOOK_NOT_FOUND_FOR_MARKET_AND_DATE;
      data: { marketId: number; asOfDate?: Date };
    }
  | {
      errorType: typeof FinanceErrorType.FINANCING_REJECTION_TICKET_MISSING;
      data: { projectId: number; automatedTicketType: string; action: string };
    }
  | { errorType: typeof FinanceErrorType.SEED_FINANCING_STILL_INITIALIZING; data: { requestForQuoteId: number } };

export const financeErrorDetails: Record<FinanceErrorType, ErrorDetails> = {
  UNKNOWN: { classification: ErrorClassification.UNEXPECTED_ERROR, sentryDescription: "Unknown Error" },
  ODDJOB_ENQUEUE_TIMEOUT: {
    classification: ErrorClassification.TIMEOUT_ERROR,
    sentryDescription: "Oddjob enqueue for Strategic Partner financing initialization exceeded its time budget",
  },
  STRATEGIC_PARTNER_BID_MISSING_ACTIVE_QUOTE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription:
      "Strategic-partner payment-method switch had no active quote version to recompute the dealer fee on",
  },
  DEAL_DESK_UNSUPPORTED_PARTNER_FLOW: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Unsupported partner flow for deal desk custom pricing",
  },
  DEAL_DESK_SUNRUN_FRP_NOT_SUPPORTED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Sunrun financially responsible party not yet supported in deal desk",
  },
  DEAL_DESK_NO_ADJUSTMENT_PATH: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Deal desk floor equals at-cost roof price, no adjustment path found",
  },
  DEAL_DESK_PRICEBOOK_PRICE_MISSING: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Pricebook price missing for deal desk calculation",
  },
  DEAL_DESK_CONTRACTOR_COST_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Contractor unit cost not found for deal desk calculation",
  },
  DEAL_DESK_COMP_SHINGLE_PRICE_MISSING: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Comp shingle pricebook price missing for deal desk calculation",
  },
  DEAL_DESK_COST_PERCENTAGE_NOT_COMPUTED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Cost percentage of price not computed for custom pricing line items",
  },
  DEAL_DESK_MISSING_MARKET_GEOGRAPHIC_AREA: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Market has no geographic area for materials ratebook lookup",
  },
  DEAL_DESK_MISSING_MATERIALS_RATEBOOK: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No published materials ratebook found for market geographic area",
  },
  DEAL_DESK_INVALID_WORKSITE_GEOJSON: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Worksite GeoJSON point failed validation for contractor lookup",
  },
  DEAL_DESK_NO_ELIGIBLE_CONTRACTORS: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No eligible contractors found for deal desk labor blending",
  },
  DEAL_DESK_NO_DEFAULT_COSTS: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription:
      "Market has no usable MARKET_DEFAULT_LABOR fallback (no geographic area, no published ratebook, or no REMI_PRO_CONTRACTOR rows), so the project has no labor cost basis (required types listed in missingDefaultCostLineItemTypes)",
  },
  DEAL_DESK_MISSING_LINE_ITEM_ID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Quote line item missing ID for deal desk calculation",
  },
  DEAL_DESK_LINE_ITEM_COST_MISMATCH: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Per-line-item cost sum diverges from quote-level cost by more than $5",
  },
  BID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Bid not found",
  },
  HOMEOWNER_EMAIL_MISSING: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Strategic Partner financing retry blocked: homeowner has no primary email",
  },
  PROJECT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Project not found for bid",
  },
  INVALID_CONTRACTOR_TYPE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid contractor type for market",
  },
  MARKET_NOT_FOUND_FOR_PROPERTY_ADDRESS_GEO_POINT: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Market not found for property address geo point",
  },
  SERVICE_AREA_NOT_FOUND_FOR_PROPERTY_ADDRESS: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Service area not found for property address",
  },
  RATEBOOK_NOT_FOUND_FOR_GEO_POINT_AND_DATE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Ratebook not found for geo point and date",
  },
  HOMEOWNER_CREDIT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Homeowner credit row not found (or wrong type)",
  },
  HOMEOWNER_CREDIT_NOT_MUTABLE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Homeowner credit transition rejected: row is past PENDING_APPROVAL",
  },
  HOMEOWNER_CREDIT_NOT_APPROVABLE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Approve rejected: homeowner credit is not in PENDING_APPROVAL",
  },
  HOMEOWNER_CREDIT_NOT_REJECTABLE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Reject rejected: homeowner credit is not in PENDING_APPROVAL",
  },
  HOMEOWNER_CREDIT_NOT_MARKABLE_SENT: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Mark sent rejected: homeowner credit is not in PENDING_PAYMENT",
  },
  HOMEOWNER_CREDIT_NOT_CONFIRMABLE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Confirm rejected: homeowner credit is not in PENDING_PAYMENT_CONFIRMATION",
  },
  APPEASEMENT_REQUIRES_BID_PROJECT: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Appeasement creation rejected: bid has no non-canceled child projects",
  },
  APPEASEMENT_INVALID_REQUESTING_TEAM: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Appeasement creation rejected: requesting team is missing, deleted, or not whitelisted",
  },
  APPEASEMENT_INVALID_ASSOCIATED_PROJECTS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Appeasement creation rejected: associated projects are empty or not active projects on the bid",
  },
  APPEASEMENT_CATEGORY_REQUIRED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Appeasement creation rejected: at least one category is required",
  },
  APPEASEMENT_INVALID_GOODWILL_AMOUNT: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Appeasement creation rejected: goodwill amount is inconsistent with the amount or categories",
  },
  APPEASEMENT_REVIEWER_UNRESOLVED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Appeasement creation blocked: no reviewer lead resolvable for a review-required appeasement",
  },
  CREATE_VENDOR_CREDITS_TASK_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "CREATE_VENDOR_CREDITS task not found (or wrong type) for createForTask",
  },
  CREATE_VENDOR_CREDITS_ATTEMPT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "CREATE_VENDOR_CREDITS task has no recorded attempt to read the source from",
  },
  VENDOR_CREDIT_INVALID_ALLOCATION: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription:
      "Vendor credit allocation rejected (see data.reason: negative, over-allocation, invalid total, or duplicate project)",
  },
  VENDOR_CREDIT_PROJECT_NOT_ALLOCATABLE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Vendor credit allocation targets a project with no assigned contractor on the bid",
  },
  VENDOR_CREDIT_MULTIPLE_PAYEES_FOR_PROJECT: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A corrections work order can be charged back to only one original contractor",
  },
  VENDOR_CREDIT_BID_NOT_FOUND_FOR_PROJECT: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No parent bid found for the corrections project backing a CREATE_VENDOR_CREDITS task",
  },
  VENDOR_CREDIT_APPEASEMENT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Appeasement not found when resolving the vendor-credit chargeback total",
  },
  RATEBOOK_NOT_FOUND_FOR_MARKET_AND_DATE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Ratebook not found for market and date",
  },
  FINANCING_REJECTION_TICKET_MISSING: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Financing rejection ticket resolved to reopen/refresh but no existing ticket was loaded",
  },
  SEED_FINANCING_STILL_INITIALIZING: {
    classification: ErrorClassification.TIMEOUT_ERROR,
    sentryDescription: "Seeded Strategic Partner financing is still initializing on oddjob",
  },
};
